import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, EmptyState, ErrorState, Skeleton } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { GapClusterRow, ResolvedGapRow, ROW_GRID } from "./gap-row";
import type { GapView } from "./gap-model";
import type { AssignableMember } from "./gap-row-menu";
import type { useResolvedGaps } from "./use-gaps";

export function GapRowsSkeleton() {
  return (
    <ul aria-hidden="true">
      {[0, 1, 2, 3].map((n) => (
        <li key={n} className={`${ROW_GRID} border-b border-line`}>
          <Skeleton className="size-4" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-[220px] max-w-full" />
            <Skeleton className="h-5 w-[360px] max-w-full" />
            <Skeleton className="h-3 w-[180px]" />
          </div>
          <Skeleton className="col-start-2 h-6 w-16 md:col-start-auto" />
          <Skeleton className="col-start-2 h-5 w-24 md:col-start-auto" />
          <Skeleton className="col-start-2 h-8 w-32 md:col-start-auto md:justify-self-end" />
        </li>
      ))}
    </ul>
  );
}

function ListHeader({ selectAll }: { selectAll: { checked: boolean | "indeterminate"; onChange: (checked: boolean) => void } | null }) {
  const { t } = useTranslation();
  return (
    <div className="hidden items-center gap-4 border-b border-line bg-surface-2 px-5 py-2.5 text-[11px] font-bold uppercase tracking-wide text-fg-subtle md:grid md:grid-cols-[2rem_1fr_9rem_8rem_15rem]">
      {selectAll ? (
        <Checkbox
          checked={selectAll.checked}
          onCheckedChange={(value) => {
            selectAll.onChange(value === true);
          }}
          aria-label={t("engage.gaps.selectAll")}
        />
      ) : (
        <span />
      )}
      <span>{t("engage.gaps.columns.cluster")}</span>
      <span>{t("engage.gaps.columns.asked")}</span>
      <span>{t("engage.gaps.columns.reason")}</span>
      <span className="text-right">{t("engage.gaps.columns.actions")}</span>
    </div>
  );
}

export interface RowHandlers {
  canResolve: boolean;
  members: readonly AssignableMember[] | null;
  memberName: (userId: string | null) => string | null;
  uploadHref: (gap: GapView) => string | null;
  onWrite: (gap: GapView) => void;
  onIgnore: (gap: GapView) => void;
  onAssign: (gap: GapView, member: AssignableMember | null) => void;
}

interface OpenPanelProps extends RowHandlers {
  gaps: readonly GapView[];
  selected: ReadonlySet<string>;
  onSelect: (ids: readonly string[], selected: boolean) => void;
  searching: boolean;
  onClearSearch: () => void;
}

export function OpenGapPanel({ gaps, selected, onSelect, searching, onClearSearch, ...rows }: OpenPanelProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  if (gaps.length === 0) {
    return searching ? (
      <EmptyState
        icon="search"
        headingLevel={3}
        title={t("engage.gaps.empty.searchTitle")}
        description={t("engage.gaps.empty.searchBody")}
        action={
          <Button variant="secondary" onClick={onClearSearch}>
            {t("engage.gaps.empty.clearSearch")}
          </Button>
        }
      />
    ) : (
      <EmptyState icon="check-circle" headingLevel={3} title={t("engage.gaps.empty.openTitle")} description={t("engage.gaps.empty.openBody")} />
    );
  }
  const ids = gaps.map((g) => g.id);
  const chosen = ids.filter((id) => selected.has(id)).length;
  const selectAll = rows.canResolve
    ? { checked: chosen === 0 ? false : chosen === ids.length ? true : ("indeterminate" as const), onChange: (value: boolean) => { onSelect(ids, value); } }
    : null;
  return (
    <>
      <ListHeader selectAll={selectAll} />
      <ul>
        {gaps.map((gap) => (
          <GapClusterRow
            key={gap.id}
            gap={gap}
            selected={selected.has(gap.id)}
            onSelectedChange={(value) => {
              onSelect([gap.id], value);
            }}
            canResolve={rows.canResolve}
            members={rows.members}
            assigneeName={rows.memberName(gap.assignee_user_id)}
            uploadHref={rows.uploadHref(gap)}
            outdatedSourceHref={gap.matched_source_id ? consoleRoutes.sourceDetail(summary.slug, gap.matched_source_id) : null}
            onWrite={() => {
              rows.onWrite(gap);
            }}
            onIgnore={() => {
              rows.onIgnore(gap);
            }}
            onAssign={(member) => {
              rows.onAssign(gap, member);
            }}
          />
        ))}
      </ul>
    </>
  );
}

export function ResolvedGapPanel({ query }: { query: ReturnType<typeof useResolvedGaps> }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  if (query.isPending) return <GapRowsSkeleton />;
  if (query.isError) return <ErrorState headingLevel={3} title={t("engage.gaps.loadError")} onRetry={() => void query.refetch()} retryLabel={t("common.retry")} />;
  const gaps = query.data.pages.flatMap((page) => page.items);
  if (gaps.length === 0) return <EmptyState icon="check-circle" headingLevel={3} title={t("engage.gaps.empty.resolvedTitle")} description={t("engage.gaps.empty.resolvedBody")} />;
  return (
    <>
      <ListHeader selectAll={null} />
      <ul>
        {gaps.map((gap) => (
          <ResolvedGapRow key={gap.id} gap={gap} sourceHref={gap.resolved_source_id ? consoleRoutes.sourceDetail(summary.slug, gap.resolved_source_id) : null} />
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2 border-t border-line px-5 py-3 text-xs text-fg-subtle">
        {t("engage.gaps.showingResolved", { shown: gaps.length })}
        {query.hasNextPage ? (
          <Button variant="ghost" size="sm" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
            {t("engage.loadMore")}
          </Button>
        ) : null}
      </div>
    </>
  );
}
